<template>
    <transition name="fade">
        <div v-if="error.length">
            <InitError :error />
        </div>

        <div
            v-else
            v-qa="'app-content'"
            v-qa.has.cookie.banner="Boolean(!cookiesDismissedCookie)"
            v-qa.segment.delay="JSON.stringify(segmentDelay)"
        >
            <AppContentTop />

            <main
                id="main"
                v-qa="'main'"
                class="main"
            >
                <slot name="default" />
            </main>

            <AppContentBottom />
        </div>
    </transition>
</template>

<script lang="ts" setup>
import { useRouter } from 'vue-router'
import { useRedirect } from 'wucdbm-vite-vue-ssr'

import { useAnalytics } from './composables/app/gtm/useAnalytics'
import {
    getIsActiveRegion,
    getRoutingRegion,
    noPrefixRegion,
    useRegionPlugin,
} from '@/plugins/region'

import { useCookiesDismissedCookie } from '@/plugins/cookie-plugin.ts'
import { useResponseError } from '@/helpers/errors'
import { useLoadSegmentOnInteractionOrDelay } from '@/third-party/segment.ts'
import { createInit, installInit } from '@/plugins/init/init.ts'
import { useTranslator } from '@/i18n'
import { useMenus } from '@/plugins/menu.ts'
import { useAuth } from '@/plugins/auth.ts'
import { useIsAdmin } from '@/components/app/isAdmin.ts'
import { useCurrentUrl, useInitialUrl } from '@/plugins/current-url.ts'
import { useApp } from '@/plugins/app.ts'
import { useBench } from '@/plugins/bench.ts'
import { useAbsoluteUrl } from '@/composables/app/useAbsoluteUrl.ts'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useMoneyFormatter } from '@/plugins/money-formatter.ts'
import { useRoutes } from '@/router/routes.ts'
import { useRouterNavigation } from '@/plugins/released-urls/useRouterNavigation.ts'

import InitError from '@/components/InitError.vue'
import AppContentTop from '@/AppContentTop.vue'
import AppContentBottom from '@/AppContentBottom.vue'
import { useCountryPlugin } from '@/plugins/country.ts'
import { type AppCurrency, useCurrencyPlugin } from '@/plugins/currency.ts'
import { computed, ref, toValue } from 'vue'
import type { CountryCode, InitQuery, InitQueryVariables } from '@/graphql/api'
import { CountrySuggestDocument } from '@/composables/app/countrySuggestQuery.generated.ts'
import { PreInitDocument } from '@/composables/app/preInitQuery.generated.ts'
import { useQuery } from '@urql/vue'
import {
    createCreateRingBuilder,
    installRingBuilder,
} from '@/plugins/ring-builder.ts'
import { useRingBuilderSetMutation } from '@/graphql/api/mutations/ringBuilderSet.mutation.generated.ts'
import { useRingBuilderStore } from '@/plugins/ring-builder-store.ts'
import { useRingBuilderDataStore } from '@/plugins/ring-builder-data-store.ts'
import { InitDocument } from '@/composables/app/useInit.generated.ts'
import { getLocaleByCountry } from '@/helpers/country-locale.ts'
import { useAutologinMutation } from '@/graphql/api/auth/autologin.mutation.generated.ts'
import {
    getCountryFromRegionCode,
    getRegionCodeForCountry,
} from '@/plugins/country2.ts'

const countryPlugin = useCountryPlugin()

const redirect = useRedirect()
const router = useRouter()
const app = useApp()
const appTranslator = useTranslator()
const menuPlugin = useMenus()
const authPlugin = useAuth()
const isAdmin = useIsAdmin()
const initialUrl = useInitialUrl()
const authToken = initialUrl.searchParams.get('authToken')
const bench = useBench()
useAnalytics()
const absoluteUrlGenerator = useAbsoluteUrl()
const currencyInput = useCurrencyInput()
const moneyFormatterNew = useMoneyFormatter()
const routes = useRoutes()
const routerNavigation = useRouterNavigation()
const ringBuilderDataStore = useRingBuilderDataStore()
const currentUrl = useCurrentUrl()
const ringBuilderCookieStore = useRingBuilderStore()
const ringBuilderSetMutation = useRingBuilderSetMutation()

const route = router.currentRoute.value

const redirectIfTrailingSlash = async (path: string) => {
    if (path !== '/' && path.endsWith('/')) {
        return redirect(path.slice(0, -1), 301)
    }
}

await redirectIfTrailingSlash(route.fullPath)

router.afterEach((route) => redirectIfTrailingSlash(route.fullPath))

// Lighthouse stops tracking script parsing in mobile performance tests
// At about 3500-3600ish timeout
// This however varies slightly
const segmentDelay = 10_000

useLoadSegmentOnInteractionOrDelay(segmentDelay)
const cookiesDismissedCookie = useCookiesDismissedCookie()

const { countryCode, countryCookie, countries, selectCountry } =
    useCountryPlugin()
const { currencies } = useCurrencyPlugin()
const { regionCode } = useRegionPlugin()

const fetchCountrySuggest =
    toValue(countryCookie) === undefined &&
    toValue(regionCode) === noPrefixRegion

const preInit = useQuery({
    query: PreInitDocument,
})

const countrySuggestQuery = useQuery({
    query: CountrySuggestDocument,
    pause: !fetchCountrySuggest,
})

const autoLoginMutation = useAutologinMutation()

const pauseInitQuery = ref(!!authToken)
const preInitPassed = ref(false)
const initQuery = useQuery<InitQuery, InitQueryVariables>({
    variables: computed((): InitQueryVariables => {
        return {
            locale: locale.value,
            isConsultant: isAdmin.value,
        }
    }),
    query: InitDocument,
    pause: computed(() => {
        return pauseInitQuery.value || !preInitPassed.value
    }),
})

await preInit

const dataStatic = toValue(preInit.data)

countries.value = dataStatic?.shippingCountries ?? []

const _currencies: AppCurrency[] = []

const flatCountries: CountryCode[] = []

for (const section of dataStatic?.shippingCountries ?? []) {
    for (const country of section.countries) {
        if (country.currency && _currencies.indexOf(country.currency) === -1) {
            _currencies.push(country.currency)
        }

        flatCountries.push(country.isoCode)
    }
}
currencies.value = _currencies

const countryCookieStatic = countryCookie.value

if (countryCookieStatic !== undefined) {
    if (!flatCountries.includes(countryCookieStatic)) {
        countryCookie.value = undefined
        await redirect(
            // @ts-expect-error alabala
            router.resolve({
                ...router.currentRoute.value,
                params: {
                    ...router.currentRoute.value.params,
                    region: getRoutingRegion(noPrefixRegion),
                },
            }).href
        )
    }
}

await countrySuggestQuery
const countrySuggestData = toValue(countrySuggestQuery.data)

if (countrySuggestData?.countrySuggest) {
    await selectCountry(countrySuggestData.countrySuggest.isoCode)
}

if (!getIsActiveRegion(regionCode.value)) {
    const countryByRegion = getCountryFromRegionCode(regionCode.value)
    await selectCountry(countryByRegion)
}

if (regionCode.value !== getRegionCodeForCountry(countryCode.value)) {
    await selectCountry(countryCode.value)
}

const locale = computed(() => getLocaleByCountry(countryCode.value))

preInitPassed.value = true
const init = createInit({
    translator: appTranslator,
    menus: menuPlugin,
    auth: authPlugin,
    isAdmin: isAdmin,
    authToken: authToken,
    bench,
    countryPlugin,
    initQuery,
    pauseInitQuery,
    autoLoginMutation,
})
installInit(app, init)

const ringBuilder = createCreateRingBuilder({
    absoluteUrlGenerator,
    translator: appTranslator,
    currency: currencyInput,
    countryPlugin,
    formatMoney: moneyFormatterNew,
    routes,
    routerNavigation,
    ringBuilderDataStore,
    ringBuilderCookieStore,
    ringBuilderSetMutation,
    currentUrl,
    bench,
})
installRingBuilder(app, ringBuilder)

const { initQueryResponse } = init

const error = useResponseError(initQueryResponse)
</script>

<style lang="scss" src="./styles/icon.scss"></style>
<style lang="scss" src="./styles/colors.scss"></style>
<style lang="scss" src="./styles/timing-functions.scss"></style>
<style lang="scss" src="./styles/reduced-motion.scss"></style>

// Unscoped styles. For elements outside the app tree (i.e. teleport targets)
<style lang="scss">
#teleport-tooltip {
    position: fixed;
    z-index: 4000;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
}

.cookies-banner {
    position: fixed;
    bottom: 15px;
    left: 15px;
    padding: 15px 15px 10px;
    max-width: 220px;
    z-index: 1050;
    background-color: $stone-2;
    box-shadow: 0 0 15px rgba($black, 0.2);

    .message {
        margin: 0 0 10px;
        font-size: 0.875rem;
        line-height: 1.2em;

        a {
            text-decoration: underline;
        }
    }

    .cookie-button-wrapper {
        width: 100%;
        display: flex;
        justify-content: flex-end;

        .dismiss-button {
            font-family: $effra-font-stack;
            letter-spacing: 0.05em;
            font-size: 0.75rem;
            text-transform: uppercase;
            font-weight: 400;
            color: $blue;
            cursor: pointer;
        }
    }
}
</style>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.45s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
